import { useEffect, useMemo, useState } from "react";

const API_BASE = "https://api.vyapakprint.com/api/distribution";

const ENDPOINTS = {
  list: () => `${API_BASE}/`,
  view: (id) => `${API_BASE}/${id}`,
  update: (id) => `${API_BASE}/${id}`,
  changePassword: (id) => `${API_BASE}/${id}/change-password`,
  delete: (id) => `${API_BASE}/${id}`,
};

export default function BusinessUsersTable() {
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({
    businessName: "",
    contactPerson: "",
    email: "",
    mobile: "",
    state: "",
    city: "",
    country: "",
    pinCode: "",
    shippingAddress: "",
    gstNumber: "",
    gstinScheme: "",
    status: "",
    userType: "",
  });
  const [savingEdit, setSavingEdit] = useState(false);

  const [passwordUser, setPasswordUser] = useState(null);
  const [passwordForm, setPasswordForm] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  function getAuthHeaders(extra = {}) {
    const token =
      localStorage.getItem("token") ||
      localStorage.getItem("adminToken") ||
      localStorage.getItem("authToken") ||
      "";

    return {
      ...extra,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  async function parseJsonSafe(res) {
    const text = await res.text();
    try {
      return text ? JSON.parse(text) : {};
    } catch {
      return { raw: text };
    }
  }

  function getUserTypeLabel(userType) {
    if (!userType) return "N/A";
    if (typeof userType === "object") {
      return userType.type || userType.name || userType._id || "N/A";
    }
    return String(userType);
  }

  function getUserTypeValue(userType) {
    if (!userType) return "";
    if (typeof userType === "object") {
      return userType._id || userType.id || userType.type || "";
    }
    return String(userType);
  }

  function extractList(payload) {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.distributions)) return payload.distributions;
    if (Array.isArray(payload?.data?.distributions))
      return payload.data.distributions;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.users)) return payload.users;
    if (Array.isArray(payload?.data?.users)) return payload.data.users;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.data?.items)) return payload.data.items;
    return [];
  }

  function extractItem(payload) {
    if (!payload) return null;
    if (payload?.distribution) return payload.distribution;
    if (payload?.data?.distribution) return payload.data.distribution;
    if (payload?.data && !Array.isArray(payload.data)) return payload.data;
    if (payload?.user) return payload.user;
    if (payload?.data?.user) return payload.data.user;
    return payload;
  }

  function normalizeUser(user = {}) {
    return {
      _id: user._id || user.id || "",
      businessName: user.businessName || user.business_name || "",
      contactPerson: user.contactPerson || user.contact_person || "",
      email: user.email || "",
      mobile: user.mobile || user.phone || "",
      state: user.state || "",
      city: user.city || "",
      country: user.country || "",
      pinCode: user.pinCode || user.pincode || "",
      shippingAddress: user.shippingAddress || user.address || "",
      gstNumber: user.gstNumber || user.gstin || "",
      gstinScheme: user.gstinScheme || user.gstScheme || "",
      status: String(user.status || "inactive").toLowerCase(),
      userType: user.userType || user.type || "",
      wallet: Number(user.wallet || user.walletBalance || 0),
      createdAt: user.createdAt || user.created_at || "",
      transactionHistory: Array.isArray(user.transactionHistory)
        ? user.transactionHistory
        : [],
      ...user,
    };
  }

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await fetch(ENDPOINTS.list(), {
        headers: getAuthHeaders(),
      });

      const data = await parseJsonSafe(res);

      if (!res.ok) {
        throw new Error(data?.message || "Failed to fetch business users.");
      }

      const list = extractList(data).map(normalizeUser);
      setUsers(list);
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
      alert(error.message || "Failed to fetch users.");
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    if (!q) return users;

    return users.filter((u) =>
      [
        u.businessName,
        u.email,
        u.contactPerson,
        u.mobile,
        u.status,
        u.state,
        u.city,
        getUserTypeLabel(u.userType),
      ].some((field) =>
        String(field || "")
          .toLowerCase()
          .includes(q),
      ),
    );
  }, [users, query]);

  async function handleView(user) {
    if (!user?._id) return;

    setBusyId(user._id);
    try {
      const res = await fetch(ENDPOINTS.view(user._id), {
        headers: getAuthHeaders(),
      });

      const data = await parseJsonSafe(res);

      if (!res.ok) {
        throw new Error(data?.message || "Could not fetch user details.");
      }

      const item = normalizeUser(extractItem(data));
      setSelected(item);
    } catch (err) {
      console.error(err);
      alert(err.message || "Could not fetch user details.");
    } finally {
      setBusyId(null);
    }
  }

  function handleOpenEdit(user) {
    const u = normalizeUser(user);

    setEditUser(u);
    setEditForm({
      businessName: u.businessName || "",
      contactPerson: u.contactPerson || "",
      email: u.email || "",
      mobile: u.mobile || "",
      state: u.state || "",
      city: u.city || "",
      country: u.country || "",
      pinCode: u.pinCode || "",
      shippingAddress: u.shippingAddress || "",
      gstNumber: u.gstNumber || "",
      gstinScheme: u.gstinScheme || "",
      status: u.status || "inactive",
      userType: getUserTypeValue(u.userType),
    });
  }

  async function handleSaveEdit() {
    if (!editUser?._id) return;

    setSavingEdit(true);
    try {
      const payload = {
        businessName: editForm.businessName,
        contactPerson: editForm.contactPerson,
        email: editForm.email,
        mobile: editForm.mobile,
        state: editForm.state,
        city: editForm.city,
        country: editForm.country,
        pinCode: editForm.pinCode,
        shippingAddress: editForm.shippingAddress,
        gstNumber: editForm.gstNumber,
        gstinScheme: editForm.gstinScheme,
        status: editForm.status,
        userType: editForm.userType || null,
      };

      const res = await fetch(ENDPOINTS.update(editUser._id), {
        method: "PUT",
        headers: getAuthHeaders({
          "Content-Type": "application/json",
        }),
        body: JSON.stringify(payload),
      });

      const data = await parseJsonSafe(res);

      if (!res.ok) {
        throw new Error(data?.message || "Failed to update user.");
      }

      const updatedUser = normalizeUser(
        extractItem(data)?.distribution ||
          extractItem(data) || { ...editUser, ...payload },
      );

      setUsers((prev) =>
        prev.map((u) => (u._id === editUser._id ? updatedUser : u)),
      );

      if (selected?._id === editUser._id) {
        setSelected(updatedUser);
      }

      setEditUser(null);
      alert(data?.message || "User details updated successfully.");
    } catch (err) {
      console.error(err);
      alert(err.message || "Could not update user.");
    } finally {
      setSavingEdit(false);
    }
  }

  function handleOpenPassword(user) {
    setPasswordUser(normalizeUser(user));
    setPasswordForm({
      newPassword: "",
      confirmPassword: "",
    });
  }

  async function handleSavePassword() {
    if (!passwordUser?._id) return;

    if (!passwordForm.newPassword || !passwordForm.confirmPassword) {
      alert("Please fill both password fields.");
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      alert("Password must be at least 6 characters.");
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert("Passwords do not match.");
      return;
    }

    setSavingPassword(true);
    try {
      const res = await fetch(ENDPOINTS.changePassword(passwordUser._id), {
        method: "PUT",
        headers: getAuthHeaders({
          "Content-Type": "application/json",
        }),
        body: JSON.stringify({
          newPassword: passwordForm.newPassword,
          confirmPassword: passwordForm.confirmPassword,
        }),
      });

      const data = await parseJsonSafe(res);

      if (!res.ok) {
        throw new Error(data?.message || "Failed to change password.");
      }

      setPasswordUser(null);
      setPasswordForm({
        newPassword: "",
        confirmPassword: "",
      });

      alert(data?.message || "Password changed successfully.");
    } catch (err) {
      console.error(err);
      alert(err.message || "Could not change password.");
    } finally {
      setSavingPassword(false);
    }
  }

  async function handleDelete(id) {
    if (!id) return;

    setBusyId(id);
    try {
      const res = await fetch(ENDPOINTS.delete(id), {
        method: "DELETE",
        headers: getAuthHeaders(),
      });

      const data = await parseJsonSafe(res);

      if (!res.ok) {
        throw new Error(data?.message || "Failed to delete.");
      }

      setUsers((prev) => prev.filter((u) => u._id !== id));
      if (selected?._id === id) setSelected(null);

      alert(data?.message || "User deleted successfully.");
    } catch (err) {
      console.error(err);
      alert(err.message || "Could not delete user.");
    } finally {
      setBusyId(null);
    }
  }

  function getStatusClasses(status) {
    const s = String(status || "").toLowerCase();

    if (s === "active") return "bg-green-100 text-green-800";
    if (s === "inactive") return "bg-gray-100 text-gray-800";
    if (s === "blocked") return "bg-red-100 text-red-800";
    if (s === "rejected") return "bg-orange-100 text-orange-800";
    return "bg-yellow-100 text-yellow-800";
  }

  function formatCurrency(value) {
    return `₹${Number(value || 0).toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    })}`;
  }

  return (
    <div className="bg-white shadow-md rounded-lg p-6">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
        <h2 className="text-2xl font-semibold">Existing Users</h2>

        <div className="flex gap-2 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Search users..."
            className="w-full sm:w-72 border px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button
            onClick={fetchUsers}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="mb-3 text-sm text-gray-500">
        Total users: <span className="font-semibold">{filtered.length}</span>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <Th>Business</Th>
              <Th>Owner</Th>
              <Th>Mobile</Th>
              <Th>State</Th>
              <Th>City</Th>
              <Th>Status</Th>
              <Th>User Type</Th>
              <Th>Wallet</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <Td colSpan="9" className="text-center">
                  Loading...
                </Td>
              </tr>
            ) : filtered.length > 0 ? (
              filtered.map((u) => (
                <tr key={u._id}>
                  <Td>
                    <div className="font-medium">{u.businessName || "N/A"}</div>
                    <div className="text-sm text-gray-500">
                      {u.email || "N/A"}
                    </div>
                  </Td>
                  <Td>{u.contactPerson || "N/A"}</Td>
                  <Td>{u.mobile || "N/A"}</Td>
                  <Td>{u.state || "N/A"}</Td>
                  <Td>{u.city || "N/A"}</Td>
                  <Td>
                    <span
                      className={`px-2 py-0.5 text-xs rounded-full font-semibold capitalize ${getStatusClasses(
                        u.status,
                      )}`}
                    >
                      {u.status || "N/A"}
                    </span>
                  </Td>
                  <Td>{getUserTypeLabel(u.userType)}</Td>
                  <Td>{formatCurrency(u.wallet)}</Td>
                  <Td className="text-right">
                    <div className="flex items-center justify-end gap-3 flex-wrap">
                      <button
                        className="text-indigo-600 hover:text-indigo-900 disabled:opacity-50"
                        onClick={() => handleView(u)}
                        disabled={busyId === u._id}
                      >
                        {busyId === u._id ? "..." : "View"}
                      </button>

                      <button
                        className="text-blue-600 hover:text-blue-900 disabled:opacity-50"
                        onClick={() => handleOpenEdit(u)}
                        disabled={busyId === u._id}
                      >
                        Edit
                      </button>

                      <button
                        className="text-amber-600 hover:text-amber-900 disabled:opacity-50"
                        onClick={() => handleOpenPassword(u)}
                        disabled={busyId === u._id}
                      >
                        Change Password
                      </button>

                      <button
                        className="text-red-600 hover:text-red-900 disabled:opacity-50"
                        onClick={() => setConfirmDeleteId(u._id)}
                        disabled={busyId === u._id}
                      >
                        {busyId === u._id ? "..." : "Delete"}
                      </button>
                    </div>
                  </Td>
                </tr>
              ))
            ) : (
              <tr>
                <Td colSpan="9" className="text-center text-gray-500">
                  No users found.
                </Td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <Modal title="Business User Details" onClose={() => setSelected(null)}>
          <div className="w-[900px] max-w-full">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
              <InfoCard label="Business" value={selected.businessName} />
              <InfoCard label="Contact" value={selected.contactPerson} />
              <InfoCard label="Mobile" value={selected.mobile} />
              <InfoCard
                label="Wallet"
                value={formatCurrency(selected.wallet)}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
              <InfoCard label="Email" value={selected.email} />
              <InfoCard label="City" value={selected.city} />
              <InfoCard label="State" value={selected.state} />
              <InfoCard label="Country" value={selected.country} />
              <InfoCard label="Pin Code" value={selected.pinCode} />
              <div className="bg-gray-50 border rounded-xl p-4">
                <p className="text-xs text-gray-500">Status</p>
                <span
                  className={`px-3 py-1 text-xs rounded-full font-semibold capitalize ${getStatusClasses(
                    selected.status,
                  )}`}
                >
                  {selected.status || "N/A"}
                </span>
              </div>
            </div>

            <div className="bg-gray-50 border rounded-xl p-4 mb-6">
              <p className="text-xs text-gray-500 mb-1">Shipping Address</p>
              <p className="font-medium text-gray-800">
                {selected.shippingAddress || "N/A"}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
              <InfoCard
                label="GST Number"
                value={selected.gstNumber || "N/A"}
              />
              <InfoCard
                label="GST Scheme"
                value={selected.gstinScheme || "N/A"}
              />
              <InfoCard
                label="User Type"
                value={getUserTypeLabel(selected.userType)}
              />
              <InfoCard
                label="Created"
                value={
                  selected.createdAt
                    ? new Date(selected.createdAt).toLocaleString()
                    : "N/A"
                }
              />
            </div>

            <div className="flex justify-end gap-3 flex-wrap">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                onClick={() => {
                  setSelected(null);
                  handleOpenEdit(selected);
                }}
              >
                Edit Details
              </button>
              <button
                className="px-4 py-2 bg-amber-500 text-white rounded-md hover:bg-amber-600"
                onClick={() => {
                  setSelected(null);
                  handleOpenPassword(selected);
                }}
              >
                Change Password
              </button>
            </div>
          </div>
        </Modal>
      )}

      {editUser && (
        <Modal title="Edit User Details" onClose={() => setEditUser(null)}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Business Name"
              value={editForm.businessName}
              onChange={(v) => setEditForm((p) => ({ ...p, businessName: v }))}
            />
            <Input
              label="Contact Person"
              value={editForm.contactPerson}
              onChange={(v) => setEditForm((p) => ({ ...p, contactPerson: v }))}
            />
            <Input
              label="Email"
              type="email"
              value={editForm.email}
              onChange={(v) => setEditForm((p) => ({ ...p, email: v }))}
            />
            <Input
              label="Mobile"
              value={editForm.mobile}
              onChange={(v) => setEditForm((p) => ({ ...p, mobile: v }))}
            />
            <Input
              label="State"
              value={editForm.state}
              onChange={(v) => setEditForm((p) => ({ ...p, state: v }))}
            />
            <Input
              label="City"
              value={editForm.city}
              onChange={(v) => setEditForm((p) => ({ ...p, city: v }))}
            />
            <Input
              label="Country"
              value={editForm.country}
              onChange={(v) => setEditForm((p) => ({ ...p, country: v }))}
            />
            <Input
              label="Pin Code"
              value={editForm.pinCode}
              onChange={(v) => setEditForm((p) => ({ ...p, pinCode: v }))}
            />
            <Input
              label="GST Number"
              value={editForm.gstNumber}
              onChange={(v) => setEditForm((p) => ({ ...p, gstNumber: v }))}
            />
            <Input
              label="GST Scheme"
              value={editForm.gstinScheme}
              onChange={(v) => setEditForm((p) => ({ ...p, gstinScheme: v }))}
            />

            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <select
                className="w-full border px-3 py-2 rounded-md"
                value={editForm.status}
                onChange={(e) =>
                  setEditForm((p) => ({ ...p, status: e.target.value }))
                }
              >
                <option value="">Select status</option>
                <option value="active">active</option>
                <option value="inactive">inactive</option>
                <option value="blocked">blocked</option>
                <option value="rejected">rejected</option>
              </select>
            </div>

            <Input
              label="User Type"
              value={editForm.userType}
              onChange={(v) => setEditForm((p) => ({ ...p, userType: v }))}
            />

            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">
                Shipping Address
              </label>
              <textarea
                className="w-full border px-3 py-2 rounded-md min-h-[100px]"
                value={editForm.shippingAddress}
                onChange={(e) =>
                  setEditForm((p) => ({
                    ...p,
                    shippingAddress: e.target.value,
                  }))
                }
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              className="px-4 py-2 bg-gray-200 rounded-md"
              onClick={() => setEditUser(null)}
              disabled={savingEdit}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50"
              onClick={handleSaveEdit}
              disabled={savingEdit}
            >
              {savingEdit ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </Modal>
      )}

      {passwordUser && (
        <Modal title="Change Password" onClose={() => setPasswordUser(null)}>
          <div className="space-y-4">
            <div className="bg-gray-50 border rounded-lg p-3">
              <p className="text-sm text-gray-500">User</p>
              <p className="font-semibold">
                {passwordUser.businessName} ({passwordUser.email})
              </p>
            </div>

            <Input
              label="New Password"
              type="password"
              value={passwordForm.newPassword}
              onChange={(v) =>
                setPasswordForm((p) => ({ ...p, newPassword: v }))
              }
            />

            <Input
              label="Confirm Password"
              type="password"
              value={passwordForm.confirmPassword}
              onChange={(v) =>
                setPasswordForm((p) => ({ ...p, confirmPassword: v }))
              }
            />
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              className="px-4 py-2 bg-gray-200 rounded-md"
              onClick={() => setPasswordUser(null)}
              disabled={savingPassword}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 bg-amber-500 text-white rounded-md disabled:opacity-50"
              onClick={handleSavePassword}
              disabled={savingPassword}
            >
              {savingPassword ? "Updating..." : "Update Password"}
            </button>
          </div>
        </Modal>
      )}

      {confirmDeleteId && (
        <Modal title="Confirm Delete" onClose={() => setConfirmDeleteId(null)}>
          <p className="mb-4">Are you sure you want to delete this user?</p>
          <div className="flex justify-end gap-3">
            <button
              className="px-4 py-2 bg-gray-200 rounded"
              onClick={() => setConfirmDeleteId(null)}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 bg-red-600 text-white rounded"
              onClick={() => {
                handleDelete(confirmDeleteId);
                setConfirmDeleteId(null);
              }}
            >
              Delete
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${className}`}
    >
      {children}
    </th>
  );
}

function Td({ children, className = "", ...rest }) {
  return (
    <td
      className={`px-6 py-4 whitespace-nowrap text-sm ${className}`}
      {...rest}
    >
      {children}
    </td>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white p-6 rounded-md shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl leading-none"
          >
            &times;
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <input
        type={type}
        className="w-full border px-3 py-2 rounded-md"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function InfoCard({ label, value }) {
  return (
    <div className="bg-gray-50 border rounded-xl p-4">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="font-medium text-gray-800">{value || "N/A"}</p>
    </div>
  );
}
